import React, { useState, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import classnames from 'classnames';
import Svg from 'svg-inline-react';
import { get } from 'lodash';
import { Container } from '@src/components/Container';
import { Button } from '@src/components/Button';
import AddPaymentMethod from '@src/containers/AddPaymentMethod';
import SkeletonLoading from '@src/components/SkeletonLoading';
import { Link } from '@src/components/Link';
import { setErrorFlashMessage } from '@src/redux/actions/flashMessages';
import { SETTINGS_COMPANY_INFORMATION_ROUTE } from '@src/Routes/links';
import { MetricAction, MetricContext, MetricEventSource } from '@src/metrics/metrics.enum';
import { trackEvent, trackOpenAddPaymentMethod } from '@src/metrics';
import FrCardSvg from '@src/svgs/fr-card.svg';
import {
  useCustomerForCardsQuery,
  useRemovePaymentMethodMutation,
  useSetDefaultPaymentMethodMutationMutation,
} from '@src/graphql/generated';
import './styles.scss';

const Cards = ({ setErrorFlashMessage }) => {
  const [cardForm, setCardForm] = useState(false);

  const { loading, error, data, refetch, networkStatus } = useCustomerForCardsQuery({
    notifyOnNetworkStatusChange: true,
    onError: () => setErrorFlashMessage(),
  });
  const [removeCard, { loading: removeCardLoading }] = useRemovePaymentMethodMutation({
    onError: () => setErrorFlashMessage(),
  });
  const [setPrimaryMethod, { loading: primaryCardLoading }] = useSetDefaultPaymentMethodMutationMutation({
    onError: () => setErrorFlashMessage(),
  });

  async function onPaymentMethodAdded() {
    // We have to update the state before refetching as otherwise the card form gets
    // rerendered which can cause issues with the braintree drop-in.
    setCardForm(false);
    await refetch();
  }

  const customerId = get(data, 'customer.id');

  if (loading || networkStatus === 4) {
    return <SkeletonLoading className="cards-loading" height="5.6rem" />;
  }

  if (error) {
    return null;
  }

  const { paysByInvoice, paymentMethods } = data.customer;
  const paymentMethodAdded = paysByInvoice || !!paymentMethods.length;

  return (
    <div className="cards">
      {!paymentMethodAdded && !cardForm && (
        <Container className="cards__add" type="info">
          <div className="cards__general-icon">
            <Svg src={FrCardSvg} />
          </div>

          <p className="cards__no-payment-info">There is no payment method</p>

          <Button
            onClick={() => {
              setCardForm(true);
              trackOpenAddPaymentMethod({
                'Event Source': MetricEventSource.ADD_NEW_PAYMENT_METHOD_BUTTON,
                'Previous Context': MetricContext.BILLING,
                'Type': 'Card',
              });
            }}
          >
            Add a payment method
          </Button>
        </Container>
      )}

      {cardForm && (
        <AddPaymentMethod
          disableInvoicing={paysByInvoice}
          metricsContext={MetricContext.BILLING}
          onPaymentMethodAdded={onPaymentMethodAdded}
        />
      )}

      {paymentMethodAdded && !cardForm && (
        <div className="cards__list">
          {paysByInvoice && (
            <Container className="cards__card" type="info">
              <div className="cards__info-left">
                <div className="cards__pay-by-invoice">You have selected to pay by invoice.</div>
              </div>

              <div className="cards__info-right">
                <Link
                  className="cards__edit-invoice"
                  onClick={() =>
                    trackOpenAddPaymentMethod({
                      'Event Source': MetricEventSource.EDIT_INVOICING_DETAILS_BUTTON,
                      'Previous Context': MetricContext.BILLING,
                      'Type': 'Invoice',
                    })
                  }
                  to={SETTINGS_COMPANY_INFORMATION_ROUTE}
                >
                  Edit invoicing details
                </Link>
              </div>
            </Container>
          )}

          {paymentMethods.map(card => (
            <Container className="cards__card" key={card.id} type="info">
              <div className="cards__info-left">
                <img className="cards__icon" src={card.imageUrl} />

                <div className="cards__number">{card.masked}</div>
              </div>
              <div className="cards__info-right">
                {card.default ? (
                  <div className="cards__primary">Primary</div>
                ) : (
                  <Fragment>
                    <div
                      className={classnames('cards__make-primary', {
                        'cards__make-primary--loading': removeCardLoading || primaryCardLoading,
                      })}
                      onClick={() => {
                        setPrimaryMethod({
                          variables: {
                            paymentMethodId: card.id,
                          },
                        });

                        trackEvent({
                          context: MetricContext.BILLING,
                          interaction: MetricAction.MAKE_CARD_PRIMARY,
                          payload: { Customer: customerId },
                        });
                      }}
                    >
                      Make primary
                    </div>

                    <div
                      className={classnames('cards__remove', {
                        'cards__remove--loading': removeCardLoading || primaryCardLoading,
                      })}
                      onClick={() => {
                        removeCard({
                          variables: {
                            paymentMethodId: card.id,
                          },
                        });

                        trackEvent({
                          context: MetricContext.BILLING,
                          interaction: MetricAction.REMOVE_CARD,
                          payload: { Customer: customerId },
                        });
                      }}
                    >
                      Remove
                    </div>
                  </Fragment>
                )}
              </div>
            </Container>
          ))}

          <div className="cards__add-new">
            <div
              className="cards__add-new-button"
              onClick={() => {
                setCardForm(true);
                trackOpenAddPaymentMethod({
                  'Event Source': MetricEventSource.ADD_NEW_PAYMENT_METHOD_BUTTON,
                  'Previous Context': MetricContext.BILLING,
                  'Type': 'Card',
                });
              }}
            >
              Add a new payment method
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

Cards.propTypes = {
  setErrorFlashMessage: PropTypes.func.isRequired,
};

export default connect(null, { setErrorFlashMessage })(Cards);
